import { createRef, useContext } from "react";
import JSONSchemaForm from "@rjsf/core";
import CrudModuleFactoryModal from "@beeldit/core/components/CrudModuleFactoryModal";
import useGet from "./useGet";
import useUpdate from "./useUpdate";
import { EditActionContext } from "./EditActionContext";
import BeelditForm from "@beeldit/core/components/BeelditForm";
import BeelditGenericForm from "@beeldit/core/components/BeelditGenericForm";

function useEditAction(
  modelConfig: any,
  index: any,
  prepareCancelFunction: any,
  prepareUncancelFunction: any
): any {
  const get = useGet(modelConfig);
  const getFunction = (id: number) => get(id);

  /**
   * Form store success function definition
   */
  const onSuccessUpdate = () => {
    setShowModal(false);
    // No llamamos a index() después de actualizar porque los datos del calendario
    // ya están cargados y no necesitan refrescarse. Esto evita problemas con filtros
    // y mejora la performance al no hacer peticiones innecesarias a la API.
  };

  const update = useUpdate(modelConfig);
  const updateFunction = () => update(onSuccessUpdate);

  /** Creation and edition modal function and state definition */
  const contextInstance = useContext(EditActionContext);
  const { showModal, setShowModal } = contextInstance;

  /** Creation and edition modal element definition */
  const { element, setElement } = useContext(EditActionContext);

  /**
   * Form Reference definition
   */
  const formRef = createRef<JSONSchemaForm>();

  /**
   * Creation and edition modal configuration definition
   */
  const modalConfig = {
    title: "Edit " + modelConfig.modelName,
    contextInstance: contextInstance,
    onSubmit: () => formRef.current?.formElement.current.requestSubmit(), //We pass the function for the submit of the modal
    fullscreen: modelConfig.modalFullScreen,
  };

  /**
   * Form configuration definition
   */
  const formConfig = {
    formRef: formRef, // We pass the ref to the form
    onSubmit: () => {
      updateFunction();
    },
    element: element, // We pass the element to the form in order to render it in the modal form
    contextInstance: contextInstance,
  };

  const leftButtons = () => {
    if (!element) {
      return null;
    }
    const printButton = (
      <button
        type="button"
        className="btn btn-success"
        onClick={() => {
          const host = process.env.REACT_APP_API_HOST;
          window.open(
            `${host}agreement/download/${element.encrypted_id}`,
            "_blank"
          );
        }}
      >
        Imprimir contrato
      </button>
    );
    let cancelButton;
    if (element.cancelled) {
      cancelButton = (
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => {
            prepareUncancelFunction(element.id);
          }}
        >
          Anular cancelación de reserva
        </button>
      );
    } else {
      cancelButton = (
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => {
            prepareCancelFunction(element.id);
          }}
        >
          Cancelar reserva
        </button>
      );
    }
    // Añadimos botón para guardar cambios y inicilizar otra reserva con el mismo cliente
    const saveAndCreateAnotherButton = (
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          update(() => {
            index(); // Refresh the table on success
            const customerId = element.customer_id;
            setElement({ customer_id: customerId }); // Reset the element to create a new one
          });
        }}
      >
        Guardar y crear otra reserva
      </button>
    );
    return (
      <>
        {cancelButton}
        {printButton}
        {saveAndCreateAnotherButton}
      </>
    );
  };

  const actionTemplate = (
    <CrudModuleFactoryModal
      modalConfig={modalConfig}
      leftButtons={leftButtons()}
    >
      <BeelditGenericForm formConfig={formConfig} modelConfig={modelConfig} />
    </CrudModuleFactoryModal>
  );

  /** END EDIT FORM ACTION */

  return {
    getFunction,
    actionTemplate,
    showModal,
  };
}

export default useEditAction;
